import { HardnessValueMap, LOCAL_STORAGE_KEYS } from "components/Viewer3d/consts";
import { GCodeSetting } from "models/GcodeSetting";
import { GCodeSettingSaveRequestDto } from "models/GCodeSettingSaveRequestDto";
import { Printer } from "models/Printer";
import { PrinterSetting } from "models/PrinterSetting";
import toast from "react-hot-toast";
import { updateDesignPrintCount } from "./designService";
import { useSelectedDesignStore } from "context/SelectedDesignContext";
import { useDesignStore } from "context/DesignContext";
import { ConditionSide } from "models/Insole";
import { PrinterHelpertSetting } from "models/PrinterHelperSetting";
import { createPrinter, PrinterType } from "Printers/PrinterFactory";
import { any } from "three/src/nodes/TSL.js";
import ApiClient from "ApiClient";
import { FetchHubTaskDto } from "models/FetchHubTaskDto";

export type UnsavedPrintCountModel = {
  userId: number;
  designId: number;
  userPrintCount: number;
  designPrintCount: number;
}
export type sendGcodeTextDto = {
  printerType: PrinterType,
  printerIp: string,
  gcodeTitle: string,
  gcodeText: string,
}

export async function sendGcodeWithUI(inputs: sendGcodeTextDto[], api: any) {
  try {
    await Promise.all(inputs.map(async (itemPrint) => {
      const printer = createPrinter(itemPrint.printerType);
      await printer.sendGcodeToPrinter(itemPrint.gcodeTitle, itemPrint.gcodeText, itemPrint.printerIp);
      toast.success(`G-code ${itemPrint.gcodeTitle} was sent successfully.`);
    }));

    increasePrintCounts();
    await UpdateUnsavedPrintCounts(api);
    return true;
  } catch (err) {
    toast.error(String(err));
    return false;
  }
}

export async function detectOS(): Promise<'Windows' | 'macOS' | 'Linux' | 'Unknown'> {
  const platform = navigator.platform.toLowerCase();
  // const userAgent = navigator.userAgent.toLowerCase();

  if (platform.includes('win')) return 'Windows';
  if (platform.includes('mac')) return 'macOS';
  if (platform.includes('linux')) return 'Linux';
  return 'Unknown';
};


export async function fetchAllPrinters(api: any): Promise<Printer[]> {
  const res = await api.get('/api/printers');
  if (res.status === 401) {
    localStorage.removeItem('accessToken');
    window.location.href = '/login';
    return [];
  }
  const mappedData: Printer[] = Array.isArray(res?.body)
    ? res.body as Printer[]
    : [];
  return mappedData;
}

// Store user's default printers to local storage
export const storeDefaultPrinters = (leftPrinterObject: any, rightPrinterObject: any) => {
  localStorage.setItem(LOCAL_STORAGE_KEYS.DEFAULT_PRINTERS,
    JSON.stringify(
      [
        {
          printerId: leftPrinterObject?.printerId,
          ip: leftPrinterObject?.ip,
          defaultPrinter: leftPrinterObject?.defaultPrinter ?? ConditionSide.left,
          settingName: leftPrinterObject?.setting.name
        },
        {
          printerId: rightPrinterObject?.printerId,
          ip: rightPrinterObject?.ip,
          defaultPrinter: rightPrinterObject?.defaultPrinter ?? ConditionSide.right,
          settingName: rightPrinterObject?.setting.name
        },
      ]
    )
  );
}

export async function saveGCodeSetting(api: any, setting: GCodeSetting): Promise<void> {
  try {
    const settingDto = new GCodeSettingSaveRequestDto({
      checkToRemove: setting.checkToRemove,
      designId: setting.designId,
      id: setting.id,
      printerId: setting.printerId,
      side: setting.side,
    });
    if (setting.insoleHardness) settingDto.insoleHardness = HardnessValueMap[setting.insoleHardness];
    if (setting.foreFootHardness) settingDto.foreFootHardness = HardnessValueMap[setting.foreFootHardness];
    if (setting.midInnerHardness) settingDto.midInnerHardness = HardnessValueMap[setting.midInnerHardness];
    if (setting.midOuterHardness) settingDto.midOuterHardness = HardnessValueMap[setting.midOuterHardness];
    if (setting.heelHardness) settingDto.heelHardness = HardnessValueMap[setting.heelHardness];
    const res = await api.put(`/api/gsetting`, setting);
    if (!res.ok) throw new Error('Failed to save gcode setting');

  } catch (error) {
    toast.error('Error saving gcode setting: ' + error);
  }
};

export async function fetchAllPrinterSettings(api: any): Promise<PrinterSetting[]> {
  const res = await api.get('/api/printers/printersetting');
  if (res.status === 401) {
    localStorage.removeItem('accessToken');
    window.location.href = '/login';
    return [];
  }
  const mappedData: PrinterSetting[] = Array.isArray(res?.body) && res.body.length > 0
    ? res.body.map((u: any) => ({ id: u.id, name: u.name }))
    : [];
  return mappedData;
}

export async function saveDefaultPrinters(api: any, leftPrinterId: number, rightPrinterId: number): Promise<void> {
  try {
    const reqObj = { IdLeft: leftPrinterId, IdRight: rightPrinterId };
    const res = await api.put('/api/printers/default', reqObj);
    if (!res.ok) throw new Error('Failed to save default printers');
  } catch (error) {
    toast.error('Error saving default printers: ' + error);
  }
}

const increasePrintCounts = () => {
  const currentDesign = useDesignStore.getState().insoleDesign;
  if (currentDesign == null) return;
  const storedUser = localStorage.getItem(LOCAL_STORAGE_KEYS.USER);
  const user = storedUser ? JSON.parse(storedUser) : null;

  if (currentDesign.designType !== 'Template') {
    const printCountModel: UnsavedPrintCountModel = {
      userId: user.id,
      designId: useSelectedDesignStore.getState().designId,
      userPrintCount: user ? user.printCount + 1 : 1,
      designPrintCount: currentDesign ? currentDesign.printCount + 1 : 1
    };
    savePrintcountsOnlocalStorage(printCountModel);
  }
}

export const savePrintcountsOnlocalStorage = (printCounts: UnsavedPrintCountModel) => {
  localStorage.setItem(LOCAL_STORAGE_KEYS.PRINT_COUNTS, JSON.stringify(printCounts));
}

export const loadPrintcountsFromlocalStorage = (): UnsavedPrintCountModel | null => {
  try {
    const localStorageString = localStorage.getItem(LOCAL_STORAGE_KEYS.PRINT_COUNTS);
    if (localStorageString) {
      const localStoragePrinterCounts = JSON.parse(localStorageString);
      return localStoragePrinterCounts;
    }
    else {
      return null;
    }
  } catch (error) {
    console.log(error);
    return null;
  }

}

export const restoreDefaultPrinters = () => {
  try {
    const localStorageString = localStorage.getItem(LOCAL_STORAGE_KEYS.DEFAULT_PRINTERS);
    if (localStorageString) {
      const localStoragePrinters = JSON.parse(localStorageString);
      // length >= 1, not > 1. Requiring two entries meant a single configured
      // printer was never restored at all: the function fell through to
      // {null, null} and every caller concluded no printer was set up.
      if (Array.isArray(localStoragePrinters) && localStoragePrinters.length >= 1) {
        return {
          leftPrinterObject: localStoragePrinters.find(p => p.defaultPrinter === ConditionSide.left || p.defaultPrinter === ConditionSide.both) ?? null,
          rightPrinterObject: localStoragePrinters.find(p => p.defaultPrinter === ConditionSide.right || p.defaultPrinter === ConditionSide.both) ?? null
        }
      }
    }
  } catch (error) {
    console.log(error)
  }
  return { leftPrinterObject: null, rightPrinterObject: null };
}

/**
 * Make sure this session has a printer configuration to print with.
 *
 * Only the login page ever asked the server for the user's printers, so anyone
 * who reached the app another way had none: generateGCode read an empty store
 * and reported that no default printer was set up, however well the machine was
 * configured. An exhibition visitor arrives exactly that way. The server does
 * create a default printer for every new non-admin account, so the
 * configuration exists — nothing was fetching it.
 *
 * The machine's own configuration wins whenever it is still there. A stand set
 * up with a real printer has to keep printing to that printer rather than to
 * the placeholder address a fresh account is given, so this only fills a gap
 * and never overwrites.
 */
export async function ensureDefaultPrintersLoaded(api: any): Promise<void> {
  const stored = restoreDefaultPrinters();
  if (stored.leftPrinterObject?.ip || stored.rightPrinterObject?.ip) return;

  try {
    const printers = await fetchAllPrinters(api);
    const left = printers.find(p =>
      p.defaultPrinter === ConditionSide.left || p.defaultPrinter === ConditionSide.both) || null;
    const right = printers.find(p =>
      p.defaultPrinter === ConditionSide.right || p.defaultPrinter === ConditionSide.both) || null;
    if (left && right) storeDefaultPrinters(left, right);

    const helpers = await fetchAllPrinterHelpers(api);
    if (helpers !== null && helpers.length > 0) storePrintHelpersAll(helpers);
  } catch (error) {
    console.log(error);
  }
}

export function restorePrintHelpers(): | (PrinterHelpertSetting | null)[] {
  const rightSetting = new PrinterHelpertSetting();
  const leftSetting = new PrinterHelpertSetting();
  try {
    const localStorageString = localStorage.getItem(LOCAL_STORAGE_KEYS.PRINT_HELPERS);
    if (localStorageString) {
      const localStoragePrintHelpers = JSON.parse(localStorageString);
      if (Array.isArray(localStoragePrintHelpers) && localStoragePrintHelpers.length > 1) {
        return localStoragePrintHelpers;
      }
    } else {
      return [null, null];
    }
  } catch (error) {
    console.log(error);
    return [null, null];
  }
  return [rightSetting, leftSetting];
}

export async function fetchAllPrinterHelpers(api: any): Promise<PrinterHelpertSetting[]> {
  const res = await api.get('/api/printers/helper_setting');
  if (!res.ok) {
    return [];
  }
  const mappedData: PrinterHelpertSetting[] = Array.isArray(res?.body)
    ? res.body as PrinterHelpertSetting[]
    : [];
  return mappedData;
}

export const storePrintHelpersAll = (obj: PrinterHelpertSetting[]) => {
  const rightHelper = obj.find(a => a.side.toLocaleLowerCase() === 'right');
  const leftHelper = obj.find(a => a.side.toLocaleLowerCase() === 'left');
  storePrintHelpers(rightHelper!, leftHelper!);
}
export const storePrintHelpers = (rightObject: PrinterHelpertSetting, leftObject: PrinterHelpertSetting) => {
  localStorage.setItem(LOCAL_STORAGE_KEYS.PRINT_HELPERS,
    JSON.stringify([
      {
        hasRamp: rightObject.hasRamp,
        cutFront: rightObject.cutFront,
        side: rightObject.side
      },
      {
        hasRamp: leftObject.hasRamp,
        cutFront: leftObject.cutFront,
        side: leftObject.side
      }
    ])
  );
}
export async function savePrinterHelper(api: any, helper: PrinterHelpertSetting) {
  try {
    const dto = {
      userId: helper.userId,
      Side: helper.side,
      HasRamp: helper.hasRamp,
      CutFront: helper.cutFront,
    };
    const res = await api.post('/api/printers/helper_setting', dto);
    if (!res.ok) {
      console.log('Failed to save PrinterHelperSetting');
    }
    return res;
  } catch (err) {
    console.error('Failed to save PrinterHelperSetting', err);
    throw err;
  }

}

export async function UpdateUnsavedPrintCounts(api: any): Promise<boolean> {
  const localPrintCounts = loadPrintcountsFromlocalStorage();
  if (localPrintCounts == null) return false;

  const design = useDesignStore.getState().insoleDesign;
  let user: any | null = null;
  const storedUser = localStorage.getItem(LOCAL_STORAGE_KEYS.USER);
  if (storedUser) {
    try {
      user = JSON.parse(storedUser);

    } catch (error) {
      console.error('Failed store user from local storage', error);
    }
  }


  try {
    const response = await api.post('/api/designs/sync-print-count', {
      userId: localPrintCounts.userId,
      designId: localPrintCounts.designId,
      userPrintCount: localPrintCounts.userPrintCount,
      designPrintCount: localPrintCounts.designPrintCount,
    });

    if (response.ok && user != null) {
      localStorage.removeItem(LOCAL_STORAGE_KEYS.PRINT_COUNTS);

      if (user.id == localPrintCounts.userId) {
        user.printCount = localPrintCounts.userPrintCount;
        localStorage.setItem(LOCAL_STORAGE_KEYS.USER, JSON.stringify(user));
        if (design != null) {
          design.printCount = localPrintCounts.designPrintCount;
        }
      }
    }
    return response.ok;

  } catch (error) {
    console.error('Failed to sync print counts', error);
    return false;
  }
}


export async function SendDesignToHubTask(api: any, designId: number){
  const res = await api.post(`/api/send_to_hub/${designId}`);
  console.log('SendDesignToHubTask', res);
}

export async function UpdateHubPrintTask(api: any, taskId: number, done: boolean) {
  const params = new URLSearchParams();
  params.append('done', done.toString());
  const res = await api.patch(`/api/send_to_hub/${taskId}?${params.toString()}`);
}

export async function FetchHubTasks(api: ApiClient): Promise<FetchHubTaskDto[]> {
  const res = await api.get(`/api/send_to_hub/tasks`);
  const mappedData: FetchHubTaskDto[] = Array.isArray(res?.body)
  ? res.body as FetchHubTaskDto[]
  : [];
  return mappedData;
}

// export async function UpdateHubPrintTaskByDesignId(api: ApiClient, designId: number, done: boolean) {
//   const hubTasks = await FetchHubTasks(api);
//   const targetTask = hubTasks.filter(
//     t => t.designId === designId && t.done === false).sort(
//     (t1, t2) => Date.parse(t1.createdAt) - Date.parse(t2.createdAt)).at(-1);
//   if (targetTask) {
//     await UpdateHubPrintTask(api, targetTask?.id, done);
//   }
// }

export async function UpdateUserPrintRequestCount(api: any, userId: number, printRequestCount: number) {
  const res = await api.patch('/api/user/', { userId: userId, printRequestCount: printRequestCount });
}
